/**
 * Generic single-order detail updater for Postgres-backed tables.
 * @module singleOrderDetailsGenericUpdateForPostgres
 */

import { API_ENDPOINTS } from "../../../enums/apiUrls";
import { SingleOrderDetailsTableName } from "../../../enums/singleOrderDetailsTableName";
import { throwIfSiblingFailures } from "../../throwApiError";
import { getBearerToken } from "../../postCall/bearerToken";

/** Parameters for updating a single order's details in a specific Postgres table. */
export interface SingleOrderDetailsGenericUpdateParams<T> {
  order_id: string;
  org_id: string;
  /** Target table/resource to update (e.g. coverage, order, clinical-data). */
  table_name: SingleOrderDetailsTableName;
  /** Partial data payload to write to the target table. */
  data: T;
}

/** Response shape returned by the Postgres update endpoint. */
export interface SingleOrderDetailsGenericUpdateResponse {
  success: boolean;
  updated_rows: number;
  /** Order ids the operation was applied to; null for single-order requests. */
  orders_updated?: string[] | null;
  /** Sibling order id -> error message for orders that failed; null when none failed. */
  sibling_failures?: Record<string, string> | null;
}

/**
 * Updates details for a single medical PA order in a specified Postgres table.
 *
 * @typeParam T - Shape of the update payload sent in the request body.
 * @param params - Order ID, org ID, target table name, and the data to update.
 * @param signal - Optional AbortSignal to cancel the in-flight request.
 * @returns The update result indicating success and number of affected rows.
 * @throws {Error} When the HTTP response is not OK, sibling propagation fails, or on network/parse errors.
 * @throws Re-throws any network or parsing error after logging.
 */
export const singleOrderDetailsGenericUpdateForPostgres = async <T>(
  params: SingleOrderDetailsGenericUpdateParams<T>,
  signal?: AbortSignal,
): Promise<SingleOrderDetailsGenericUpdateResponse> => {
  try {
    const url = `${API_ENDPOINTS.SINGLE_ORDER_DETAILS_GENERIC_CALL_FOR_POSTGRES_UPDATE}/${params.table_name}`;
    const bearerToken = await getBearerToken();
    const raw = JSON.stringify({
      order_id: params.order_id,
      org_id: params.org_id,
      data: params.data,
    });
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
      body: raw,
      ...(signal && { signal }),
    });
    const result: SingleOrderDetailsGenericUpdateResponse =
      await response.json();
    if (!response.ok) {
      throw new Error(
        `Postgres update request failed (${response.status}): ${JSON.stringify(result)}`,
      );
    }
    throwIfSiblingFailures(result);
    return result;
  } catch (error: any) {
    if (error.name !== "AbortError") {
      console.error(error);
    }
    throw error;
  }
};
